// frontend/src/components/common/PrivateRoute.js
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import AppShell from './AppShell';

/**
 * UPGRADED PrivateRoute Component
 * This component handles both a single `role` prop (string) for simple routes
 * and a `roles` prop (array of strings) for routes accessible by multiple user types.
 * It is fully backward-compatible and provides intelligent redirects.
 */
const PrivateRoute = ({ children, user, role: requiredRole, roles: requiredRoles }) => {
    const location = useLocation();

    // 1. If the user is not logged in, always redirect to the login page.
    if (!user) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    const { role: userRole } = user;

    // 2. Determine if the user is authorized.
    let isAuthorized = false;
    const allRequiredRoles = requiredRoles || (requiredRole ? [requiredRole] : []);

    if (allRequiredRoles.length > 0) {
        // Check if the user's role is included in the list of required roles.
        // This handles simple cases like 'parent', 'teacher', etc.
        if (allRequiredRoles.includes(userRole)) {
            isAuthorized = true;
        } else {
            // Handle special group roles like 'admin' which covers multiple DB roles.
            const isAdminRoute = allRequiredRoles.some(r => ['admin', 'super-admin', 'moderator'].includes(r));
            const userIsAdmin = ['admin', 'super-admin', 'moderator'].includes(userRole);
            if (isAdminRoute && userIsAdmin) {
                isAuthorized = true;
            }
        }
    }


    // 3. If authorized, render the requested component inside the app shell
    //    (green sidebar + topbar). Login / maintenance are outside PrivateRoute,
    //    so they correctly stay shell-less.
    if (isAuthorized) {
        return <AppShell>{children}</AppShell>;
    }

    // 4. If not authorized, redirect them to their default dashboard.
    let redirectPath = '/login'; // Fallback
    switch (userRole) {
        case 'parent':
            redirectPath = '/dashboard';
            break;
        case 'teacher':
            redirectPath = '/teacher/dashboard';
            break;
        case 'admin':
        case 'super-admin':
        case 'moderator':
            redirectPath = '/admin';
            break;
        default:
            redirectPath = '/login';
            break;
    }

    return <Navigate to={redirectPath} replace />;
};

export default PrivateRoute;